import type { Finding, FindingDetail, FindingStatus, Severity } from "@riea-water/contracts";

/** The prototype's pill tone per severity (SEVP) and its stripe colour (SEVC). */
export const SEVERITY_TONE: Record<Severity, string> = {
  critical: "crit",
  high: "warn",
  medium: "vio",
  low: "off",
};
export const SEVERITY_COLOUR: Record<Severity, string> = {
  critical: "var(--crit)",
  high: "var(--warn)",
  medium: "var(--violet)",
  low: "var(--faint)",
};

export const STATUS_LABEL: Record<FindingStatus, string> = {
  open: "Open",
  triaged: "Triaged",
  assigned: "Assigned",
  in_progress: "In progress",
  awaiting_party: "Awaiting party",
  resolved: "Resolved",
  verified: "Verified",
  dismissed: "Dismissed",
  reopened: "Reopened",
  auto_cleared: "Auto-cleared",
};

const STATUS_TONE: Record<FindingStatus, string> = {
  open: "warn",
  triaged: "info",
  assigned: "info",
  in_progress: "info",
  awaiting_party: "vio",
  resolved: "ok",
  verified: "ok",
  dismissed: "off",
  reopened: "warn",
  auto_cleared: "off",
};

export function statusTone(status: FindingStatus): string {
  return STATUS_TONE[status];
}

export type FindingTab = "all" | "team" | "awaiting" | "overdue";

export const TABS: {
  key: FindingTab;
  label: string;
  view: "all" | "my_team" | "awaiting_party" | "overdue";
}[] = [
  { key: "all", label: "All", view: "all" },
  { key: "team", label: "My team", view: "my_team" },
  { key: "awaiting", label: "Awaiting other party", view: "awaiting_party" },
  { key: "overdue", label: "Overdue", view: "overdue" },
];

/** The prototype's SLA text: late in red, five days or fewer in amber, otherwise muted. */
export function slaText(finding: Pick<Finding, "daysToSla" | "slaLate" | "status">): {
  label: string;
  tone: "late" | "soon" | "calm";
} {
  if (
    finding.status === "resolved" ||
    finding.status === "verified" ||
    finding.status === "dismissed" ||
    finding.status === "auto_cleared"
  )
    return { label: "closed", tone: "calm" };
  if (finding.slaLate) return { label: `${Math.abs(finding.daysToSla)} days late`, tone: "late" };
  if (finding.daysToSla <= 5) return { label: `${finding.daysToSla} days`, tone: "soon" };
  return { label: `${finding.daysToSla} days`, tone: "calm" };
}

export const EVIDENCE_KIND_LABEL: Record<string, string> = {
  photo: "Photo",
  document: "Document",
  read_record: "Read record",
  field_record: "Field record",
  correspondence: "Correspondence",
  market_response: "Market response",
  gate_record: "Gate record",
};

/** Facts worth a row on the record: the rule's own values, humanised, without bookkeeping keys. */
const HIDDEN_FACTS = new Set(["evidence_ids", "readability", "failing", "test"]);

export function factRows(facts: Record<string, unknown>): { label: string; value: string }[] {
  return Object.entries(facts)
    .filter(
      ([key, value]) =>
        !HIDDEN_FACTS.has(key) && value !== null && value !== undefined && value !== "",
    )
    .map(([key, value]) => ({ label: humanise(key), value: factText(value) }));
}

export function humanise(key: string): string {
  const text = key.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2}))?$/;

export function factText(value: unknown): string {
  if (typeof value === "string" && ISO.test(value)) return shortDate(value);
  if (typeof value === "number")
    return Number.isInteger(value)
      ? value.toLocaleString("en-GB")
      : value.toLocaleString("en-GB", { maximumFractionDigits: 1 });
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(factText).join(", ");
  return JSON.stringify(value);
}

export function shortDate(iso: string): string {
  return new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

/** How many route steps show as done, as the prototype draws it. */
export function stepsDone(status: FindingStatus, steps: number): number {
  if (status === "resolved" || status === "verified") return steps;
  if (status === "in_progress" || status === "awaiting_party") return 1;
  return 0;
}

/** The evidence kinds still needed before Resolve unlocks. */
export function missingKinds(detail: Pick<FindingDetail, "closeRequires" | "evidence">): string[] {
  const have = new Set(detail.evidence.map((e) => e.kind));
  return detail.closeRequires.filter((k) => !have.has(k));
}

export function impactLine(f: Pick<Finding, "impactFactors">): string {
  const x = f.impactFactors;
  return `${x.severityWeight} × ${x.exposureFactor} × ${x.volumeFactor.toFixed(1)} × ${x.ageFactor.toFixed(2)} ÷ ${x.effortWeight}`;
}
